import { ValidSource } from "@validator/schema";
import { createContext, useContext, useState, useCallback, ReactNode } from "react";

function createViewContext<T extends { view: string }>(defaultState: T) {
  const Context = createContext<{
    state: T;
    setState: React.Dispatch<React.SetStateAction<T>>;
    setView: (view: T["view"]) => void;
  } | null>(null);

  function Provider({ children, initial }: { children: ReactNode; initial?: T }) {
    const [state, setState] = useState<T>(initial ?? defaultState);

    const setView = useCallback((view: T["view"]) => setState((prev) => ({ ...prev, view })), []);

    return <Context.Provider value={{ state, setState, setView }}>{children}</Context.Provider>;
  }

  function useView() {
    const ctx = useContext(Context);
    if (!ctx) throw new Error("useView must be used within its Provider");
    return ctx;
  }

  return { Provider, useView };
}

/** Study View **/

export interface StudyViewState {
  view: "init" | "submission" | "success" | "report" | "closed";
  instructionsTab: ValidSource;
}

export const { Provider: StudyViewProvider, useView: useStudyView } =
  createViewContext<StudyViewState>({
    view: "init",
    instructionsTab: "chatgpt",
  });

/** Insights View **/

export interface InsightsViewState {
  view: "init" | "consent" | "submission" | "report";
  instructionsTab: ValidSource;
}

export const { Provider: InsightsViewProvider, useView: useInsightsView } =
  createViewContext<InsightsViewState>({
    view: "init",
    instructionsTab: "chatgpt",
  });
